import { useState } from 'react';
import { toast } from 'react-toastify';
import type { InstallView, UsageStatement } from '@dunea/shared';
import {
  apiGetFunctionHealth,
  apiGetListing,
  apiGetUsage,
  apiUninstall,
  apiUpdateInstall,
} from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  LoadError,
  Pill,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource, type Resource } from '~/components/workbench/tools/payments/use-resource';
import { formatDate } from '~/components/workbench/tools/marketing/marketing-model';
import { requestApplyTheme } from './install-actions';
import {
  apiErrorMessage,
  creditsLabel,
  creditsNumber,
  functionFailuresLabel,
  initialSettingsValues,
  installStatusMeta,
  kindLabel,
  monthKey,
  parseSpendCap,
  SPEND_CAP_SENTENCE,
  spendSummary,
  usageFor,
  listingSettingsFields,
  permissionLabel,
  permissionsAfterApproval,
  priceLabel,
  settingsErrors,
  settingsPayload,
} from './marketplace-model';
import { ListingIcon } from './parts';
import { SettingsForm } from './SettingsForm';
import { AdminExtensionSlot } from '~/components/workbench/admin-extensions';
import { invalidateAdminExtensions } from '~/lib/api/admin-extensions';

const lastSignature = new Map<string, string>();

function installsSignature(rows: readonly InstallView[]): string {
  return rows.map((i) => `${i.id}:${i.status}:${i.version}:${i.grantedPermissions.length}`).join('|');
}

/** What's installed on this app: status, new permissions, settings, panel, uninstall. */
export function InstalledView({
  installs,
  onChanged,
  onOpenListing,
  onOpenPanel,
  onBrowse,
  appId,
}: {
  appId: string;
  installs: Resource<InstallView[]>;
  onChanged: (next: InstallView[]) => void;
  onOpenListing: (slug: string) => void;
  onOpenPanel: (install: InstallView) => void;
  onBrowse: () => void;
}) {
  const month = monthKey();
  const usage = useResource(() => apiGetUsage(appId, month), `${appId}:${month}`);
  // Checkout functions that fell back today (timeouts, errors): shown on their plugin's row.
  const functions = useResource(() => apiGetFunctionHealth(appId).catch(() => []), appId);

  if (installs.error && !installs.data) {
    return <LoadError message="Could not load what's installed." onRetry={() => void installs.reload()} />;
  }

  if (!installs.data) {
    return (
      <div className="space-y-2.5">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  const rows = installs.data.filter((i) => i.status !== 'uninstalled');

  // What's installed changed (install, uninstall, upgrade, permissions): admin extension lists are stale.
  if (lastSignature.get(appId) !== installsSignature(rows)) {
    lastSignature.set(appId, installsSignature(rows));
    invalidateAdminExtensions(appId);
  }

  if (rows.length === 0) {
    return (
      <div className={classNames(CARD, 'space-y-3 px-4 py-6 text-center')}>
        <p className="text-[12px] text-muted">Nothing installed on this app yet.</p>
        <button type="button" onClick={onBrowse} className={BTN_SECONDARY}>
          Browse plugins
        </button>
      </div>
    );
  }

  const replace = (next: InstallView) => onChanged(installs.data!.map((i) => (i.id === next.id ? next : i)));
  const remove = (id: string) => onChanged(installs.data!.filter((i) => i.id !== id));

  return (
    <div className="space-y-2.5">
      {/* App-level admin extensions (app.dashboard): installed plugins' overview cards. */}
      <AdminExtensionSlot key={installsSignature(rows)} appId={appId} target="app.dashboard" className="pb-2" />
      {rows.map((install) => (
        <InstallRow
          key={install.id}
          appId={appId}
          install={install}
          usage={usageFor(usage.data, install.id)}
          functionFailures={functions.data?.find((f) => f.installId === install.id)?.failuresToday ?? 0}
          onUpdated={replace}
          onRemoved={() => remove(install.id)}
          onOpenListing={() => onOpenListing(install.slug)}
          onOpenPanel={() => onOpenPanel(install)}
        />
      ))}
    </div>
  );
}

type Expanded = 'none' | 'permissions' | 'settings' | 'spend';

type UsageRow = UsageStatement['byInstall'][number];

function InstallRow({
  appId,
  install,
  usage,
  functionFailures,
  onUpdated,
  onRemoved,
  onOpenListing,
  onOpenPanel,
}: {
  appId: string;
  install: InstallView;
  usage: UsageRow | null;
  functionFailures: number;
  onUpdated: (next: InstallView) => void;
  onRemoved: () => void;
  onOpenListing: () => void;
  onOpenPanel: () => void;
}) {
  const [expanded, setExpanded] = useState<Expanded>('none');
  const meta = installStatusMeta(install.status);
  const active = install.status === 'active';
  const charges = install.metered;
  const spend = spendSummary(install);
  const toggle = (next: Expanded) => setExpanded((cur) => (cur === next ? 'none' : next));

  return (
    <div className={classNames(CARD, 'space-y-3 p-3.5')}>
      <div className="flex items-start gap-3">
        <button type="button" onClick={onOpenListing} aria-label={`${install.name} details`}>
          <ListingIcon url={install.iconUrl} name={install.name} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={onOpenListing}
              className="truncate text-left text-[13px] font-medium text-primary hover:underline"
            >
              {install.name}
            </button>
            <span className="flex shrink-0 items-center gap-1.5">
              {install.channel === 'dev' && <Pill tone="info" label="Dev" />}
              <Pill tone={meta.tone} label={meta.label} />
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-2.5 text-[11px] text-muted">
            <span>v{install.version}</span>
            <span>{kindLabel(install.kind)}</span>
            <span>{priceLabel(install.pricing)}</span>
            {install.paidThrough && <span>Paid through {formatDate(install.paidThrough)}</span>}
          </div>
        </div>
      </div>

      {install.status === 'pending_payment' && (
        <p className="text-[11.5px] leading-relaxed text-warning">
          Waiting for payment. It activates as soon as checkout completes.
        </p>
      )}

      {functionFailures > 0 && (
        <p className="flex items-start gap-1.5 text-[11.5px] leading-relaxed text-warning">
          <span className="i-ph:warning mt-0.5 shrink-0 text-[12px]" />
          <span>{functionFailuresLabel(functionFailures)}</span>
        </p>
      )}
      {install.status === 'suspended' && (
        <p className="text-[11.5px] leading-relaxed text-danger">
          Dunea suspended this listing. It's paused on your app until it's reviewed.
        </p>
      )}

      {charges && (
        <button
          type="button"
          onClick={() => toggle('spend')}
          className="block w-full space-y-1.5 text-left"
          aria-expanded={expanded === 'spend'}
        >
          <div className="flex items-center justify-between gap-2 text-[11.5px]">
            <span className={spend.reached ? 'text-warning' : 'text-secondary'}>{spend.text}</span>
            <span className="text-muted">{expanded === 'spend' ? 'Hide' : 'Usage and cap'}</span>
          </div>
          {install.spendCapCredits !== null && (
            <div className="h-1 overflow-hidden rounded-full bg-sunken">
              <div
                className={classNames('h-full rounded-full', spend.reached ? 'bg-warning' : 'bg-accent/70')}
                style={{ width: `${Math.round(spend.ratio * 100)}%` }}
              />
            </div>
          )}
        </button>
      )}

      {install.pendingPermissions.length > 0 && (
        <div className="ui-banner-warning items-center justify-between gap-2">
          <span className="text-[11.5px] text-warning">
            A new version needs {install.pendingPermissions.length} more{' '}
            {install.pendingPermissions.length === 1 ? 'permission' : 'permissions'}.
          </span>
          <button type="button" onClick={() => toggle('permissions')} className={BTN_GHOST}>
            Review new permissions
          </button>
        </div>
      )}

      {expanded === 'permissions' && (
        <PermissionsReview appId={appId} install={install} onUpdated={onUpdated} onDone={() => setExpanded('none')} />
      )}
      {expanded === 'spend' && <SpendPanel appId={appId} install={install} usage={usage} onUpdated={onUpdated} />}
      {expanded === 'settings' && (
        <SettingsEditor appId={appId} install={install} onUpdated={onUpdated} onDone={() => setExpanded('none')} />
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {install.kind === 'theme' && active && (
          <button type="button" onClick={() => requestApplyTheme(install.name, install.slug)} className={BTN_SECONDARY}>
            <div className="i-ph:magic-wand text-[13px]" />
            Apply to this app
          </button>
        )}
        {install.hasPanel && active && (
          <button type="button" onClick={onOpenPanel} className={BTN_SECONDARY}>
            <div className="i-ph:sidebar-simple text-[13px]" />
            Open panel
          </button>
        )}
        {install.kind === 'plugin' && (
          <button type="button" onClick={() => toggle('settings')} className={BTN_GHOST}>
            <div className="i-ph:gear-six text-[13px]" />
            Settings
          </button>
        )}
        <div className="flex-1" />
        <UninstallButton appId={appId} install={install} onRemoved={onRemoved} />
      </div>
    </div>
  );
}

/** This month's charges for one install, and its editable monthly cap. */
function SpendPanel({
  appId,
  install,
  usage,
  onUpdated,
}: {
  appId: string;
  install: InstallView;
  usage: UsageRow | null;
  onUpdated: (next: InstallView) => void;
}) {
  const [capText, setCapText] = useState(install.spendCapCredits === null ? '' : String(install.spendCapCredits));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cap = parseSpendCap(capText);
  const unchanged = cap.value !== null && cap.value === install.spendCapCredits;

  const save = async () => {
    setTried(true);

    if (cap.value === null) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const next = await apiUpdateInstall(appId, install.id, { spendCapCredits: cap.value });
      toast.success('Spend cap saved.');
      onUpdated(next);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the spend cap.'));
    } finally {
      setBusy(false);
    }
  };

  const charges = usage?.charges ?? [];

  return (
    <div className="space-y-3 rounded-lg border border-hairline bg-sunken p-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        className="space-y-1.5"
      >
        <h5 className={CAPTION}>Monthly spend cap</h5>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            inputMode="numeric"
            value={capText}
            onChange={(e) => setCapText(e.target.value)}
            disabled={busy}
            aria-label="Monthly spend cap in credits"
            className="ui-input w-[110px] !py-1.5 [font-variant-numeric:tabular-nums]"
          />
          <span className="text-[12px] text-secondary">credits</span>
          <button type="submit" disabled={busy || unchanged} className={BTN_SECONDARY}>
            {busy && <Spinner />}
            Save
          </button>
        </div>
        <p className="text-[11px] leading-relaxed text-muted">{SPEND_CAP_SENTENCE}</p>
        {tried && cap.error && <p className="text-[11px] text-danger">{cap.error}</p>}
        {error && <ErrorBanner message={error} />}
      </form>

      <div className="space-y-1.5">
        <h5 className={CAPTION}>This month</h5>
        {charges.length === 0 ? (
          <p className="text-[11.5px] text-muted">No charges this month.</p>
        ) : (
          <ul className="divide-y divide-hairline">
            {charges.map((c) => (
              <li key={c.id} className="flex items-start justify-between gap-3 py-1.5 text-[11.5px]">
                <span className="min-w-0">
                  <code className="font-geist-mono text-[10.5px] text-primary">{c.item}</code>
                  <span className="text-muted">
                    {' '}
                    · {c.source === 'tool' ? 'AI tool' : 'Usage'}
                    {c.units !== 1 && ` · ${creditsNumber(c.units)} units`} · {formatDate(c.at)}
                  </span>
                  {c.description && <span className="block text-muted">{c.description}</span>}
                </span>
                <span
                  className={classNames(
                    'shrink-0 [font-variant-numeric:tabular-nums]',
                    c.status === 'charged' ? 'text-primary' : 'text-muted line-through',
                  )}
                  title={c.status === 'charged' ? undefined : c.status === 'refunded' ? 'Refunded' : 'Waived'}
                >
                  {creditsLabel(c.credits)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {usage && (
          <p className="text-right text-[11px] text-secondary [font-variant-numeric:tabular-nums]">
            Total {creditsLabel(usage.usedCredits)}
          </p>
        )}
      </div>
    </div>
  );
}

function PermissionsReview({
  appId,
  install,
  onUpdated,
  onDone,
}: {
  appId: string;
  install: InstallView;
  onUpdated: (next: InstallView) => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approve = async () => {
    setBusy(true);
    setError(null);

    try {
      const next = await apiUpdateInstall(appId, install.id, { permissions: permissionsAfterApproval(install) });
      toast.success(`${install.name} updated.`);
      onUpdated(next);
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update permissions.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5 rounded-lg border border-hairline bg-sunken p-3">
      <h5 className={CAPTION}>New permissions</h5>
      <ul className="space-y-1.5">
        {install.pendingPermissions.map((id) => (
          <li key={id} className="flex items-start gap-2 text-[12px] leading-relaxed text-primary">
            <div className="i-ph:plus-circle mt-0.5 shrink-0 text-[13px] text-warning" />
            <span>
              {permissionLabel(id)} <code className="font-geist-mono text-[10px] text-muted">{id}</code>
            </span>
          </li>
        ))}
      </ul>
      {error && <ErrorBanner message={error} />}
      <div className="flex gap-2">
        <button type="button" onClick={() => void approve()} disabled={busy} className={BTN_SECONDARY}>
          {busy && <Spinner />}I allow {install.name} to do this
        </button>
        <button type="button" onClick={onDone} disabled={busy} className={BTN_GHOST}>
          Not now
        </button>
      </div>
    </div>
  );
}

function SettingsEditor({
  appId,
  install,
  onUpdated,
  onDone,
}: {
  appId: string;
  install: InstallView;
  onUpdated: (next: InstallView) => void;
  onDone: () => void;
}) {
  // InstallView carries values, not the field definitions; those come from the listing.
  const detail = useResource(() => apiGetListing(install.slug), install.slug);

  if (detail.error) {
    return <LoadError message="Could not load this plugin's settings." onRetry={() => void detail.reload()} />;
  }

  if (!detail.data) {
    return <SkeletonCard />;
  }

  const fields = listingSettingsFields(detail.data);

  if (fields.length === 0) {
    return <p className="text-[11.5px] text-muted">This plugin has no settings.</p>;
  }

  return <SettingsEditorForm appId={appId} install={install} fields={fields} onUpdated={onUpdated} onDone={onDone} />;
}

function SettingsEditorForm({
  appId,
  install,
  fields,
  onUpdated,
  onDone,
}: {
  appId: string;
  install: InstallView;
  fields: ReturnType<typeof listingSettingsFields>;
  onUpdated: (next: InstallView) => void;
  onDone: () => void;
}) {
  const [values, setValues] = useState(() => initialSettingsValues(fields, install.settings));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errors = settingsErrors(fields, values, install.secretsSet);

  const save = async () => {
    setTried(true);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const next = await apiUpdateInstall(appId, install.id, { settings: settingsPayload(fields, values) });
      toast.success('Settings saved.');
      onUpdated(next);
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save settings.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      className="space-y-3 rounded-lg border border-hairline bg-sunken p-3"
    >
      <SettingsForm
        fields={fields}
        values={values}
        errors={errors}
        secretsSet={install.secretsSet}
        onChange={setValues}
        disabled={busy}
        showErrors={tried}
      />
      {error && <ErrorBanner message={error} />}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className={BTN_SECONDARY}>
          {busy && <Spinner />}
          Save settings
        </button>
        <button type="button" onClick={onDone} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function UninstallButton({
  appId,
  install,
  onRemoved,
}: {
  appId: string;
  install: InstallView;
  onRemoved: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const uninstall = async () => {
    setBusy(true);

    try {
      await apiUninstall(appId, install.id);
      toast.success(`${install.name} uninstalled.`);
      onRemoved();
    } catch (err) {
      toast.error(apiErrorMessage(err, `Could not uninstall ${install.name}.`));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return confirming ? (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11.5px] text-secondary">Remove from this app?</span>
      <button
        type="button"
        onClick={() => void uninstall()}
        disabled={busy}
        className={classNames(BTN_SECONDARY, '!border-danger/40 !text-danger')}
      >
        {busy && <Spinner />}
        Uninstall
      </button>
      <button type="button" onClick={() => setConfirming(false)} disabled={busy} className={BTN_GHOST}>
        Keep
      </button>
    </div>
  ) : (
    <button type="button" onClick={() => setConfirming(true)} className={BTN_GHOST}>
      <div className="i-ph:trash text-[13px]" />
      Uninstall
    </button>
  );
}
